import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Shield, UserX, UserCheck, KeyRound, Eye, Trash2, Copy } from "lucide-react";

export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [tempPwd, setTempPwd] = useState("");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      const status = u.status || "active";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const updateRole = async (uid, role) => {
    try {
      await api.put(`/admin/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const doAction = async (uid, action) => {
    try {
      await api.post(`/admin/users/${uid}/action`, { action });
      toast.success(`User ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const doResetPassword = async () => {
    try {
      const { data } = await api.post(`/admin/users/${resetUser.id}/action`, { action: "reset_password" });
      setTempPwd(data.temporary_password);
      toast.success("Temporary password generated — share with user securely");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const doDelete = async () => {
    try {
      await api.post(`/admin/users/${confirmDelete.id}/action`, { action: "delete" });
      toast.success("User deleted (history preserved)");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/users/${uid}/detail`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const statusBadge = (s) => {
    const map = {
      active: "bg-emerald-100 text-emerald-800",
      suspended: "bg-rose-100 text-rose-800",
      deleted: "bg-gray-200 text-gray-600",
    };
    return map[s || "active"];
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-36" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="student">Student</SelectItem>
            <SelectItem value="teacher">Teacher</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border/70">
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const status = u.status || "active";
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs">{u.email}</TableCell>
                    <TableCell>
                      <Select defaultValue={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                        <SelectTrigger className="h-8 w-28" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="teacher">Teacher</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${statusBadge(status)}`}>{status}</span></TableCell>
                    <TableCell className="text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openDetail(u.id)} title="View details" data-testid={`view-${u.id}`}><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => { setResetUser(u); setTempPwd(""); setResetOpen(true); }} title="Reset password" data-testid={`reset-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                      {status === "suspended" ? (
                        <Button size="icon" variant="ghost" onClick={() => doAction(u.id, "activate")} title="Activate" data-testid={`activate-${u.id}`}><UserCheck className="h-4 w-4 text-emerald-600" /></Button>
                      ) : status === "active" ? (
                        <Button size="icon" variant="ghost" onClick={() => doAction(u.id, "suspend")} title="Suspend" data-testid={`suspend-${u.id}`}><UserX className="h-4 w-4 text-amber-600" /></Button>
                      ) : null}
                      <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(u)} title="Delete" disabled={status === "deleted"} data-testid={`delete-${u.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="p-10 text-center text-sm text-muted-foreground">No users match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>User profile</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-secondary/40 p-4">
                <div className="font-display text-lg font-bold">{detail.user.name}</div>
                <div className="text-sm text-muted-foreground">{detail.user.email}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-white px-2 py-0.5">Role: <b>{detail.user.role}</b></span>
                  <span className="rounded-full bg-white px-2 py-0.5">Status: <b>{detail.user.status || "active"}</b></span>
                  <span className="rounded-full bg-white px-2 py-0.5">Joined: {new Date(detail.user.created_at).toLocaleDateString()}</span>
                  {detail.user.last_login_at && <span className="rounded-full bg-white px-2 py-0.5">Last login: {new Date(detail.user.last_login_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="rounded-lg bg-secondary/40 p-3"><div className="font-mono text-lg font-bold">{detail.stats.attempts}</div><div className="text-[10px] uppercase text-muted-foreground">Attempts</div></div>
                <div className="rounded-lg bg-secondary/40 p-3"><div className="font-mono text-lg font-bold">{detail.stats.questions_answered}</div><div className="text-[10px] uppercase text-muted-foreground">Qs answered</div></div>
                <div className="rounded-lg bg-secondary/40 p-3"><div className="font-mono text-lg font-bold">{detail.stats.accuracy}%</div><div className="text-[10px] uppercase text-muted-foreground">Accuracy</div></div>
                <div className="rounded-lg bg-secondary/40 p-3"><div className="font-mono text-lg font-bold">{detail.stats.best_score}%</div><div className="text-[10px] uppercase text-muted-foreground">Best</div></div>
              </div>
              <div>
                <div className="mb-2 font-display text-sm font-bold">Enrolled courses ({detail.enrollments.length})</div>
                <div className="text-xs text-muted-foreground">
                  {detail.enrollments.length === 0 ? "No enrollments" : detail.enrollments.map((e) => e.course_id).join(", ")}
                </div>
              </div>
              <div>
                <div className="mb-2 font-display text-sm font-bold">Recent attempts</div>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Date</TableHead><TableHead>Mode</TableHead><TableHead>Score</TableHead><TableHead>%</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {detail.attempts.slice(0, 20).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="text-xs">{new Date(a.submitted_at).toLocaleString()}</TableCell>
                          <TableCell className="capitalize text-xs">{a.mode}</TableCell>
                          <TableCell className="text-xs">{a.score}/{a.total}</TableCell>
                          <TableCell className="font-mono text-xs">{a.percentage}%</TableCell>
                        </TableRow>
                      ))}
                      {detail.attempts.length === 0 && (
                        <TableRow><TableCell colSpan={4} className="p-4 text-center text-xs text-muted-foreground">No attempts</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) { setTempPwd(""); setResetUser(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password for {resetUser?.name}</DialogTitle>
            <DialogDescription>
              Generate a one-time temporary password. The user must change it after logging in. Share this securely — PilotYatra will not display it again.
            </DialogDescription>
          </DialogHeader>
          {!tempPwd ? (
            <Button onClick={doResetPassword} className="w-full bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90" data-testid="confirm-reset-btn">
              <KeyRound className="mr-2 h-4 w-4" /> Generate temporary password
            </Button>
          ) : (
            <div className="rounded-lg border border-dashed border-[hsl(var(--brand-orange))] bg-[hsl(var(--brand-orange))]/5 p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Temporary password</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 rounded bg-white px-3 py-2 font-mono text-sm" data-testid="temp-password">{tempPwd}</code>
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard.writeText(tempPwd); toast.success("Copied"); }}><Copy className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">Share this securely (via a trusted channel). PilotYatra will not show this password again.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the account as deleted and blocks login. Quiz history, bookmarks, and enrollment records are preserved for audit purposes. This action cannot be undone from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
